/**
 * THE FILTER REDUCER — pure, and the only place the filter object changes.
 *
 * Two things live here and nothing else does:
 *
 *   `apply()`      — what each action means.
 *   `normalise()`  — the invariants that must hold whatever the action was.
 *
 * The split matters. Every one of the invariants below is a bug somebody has
 * already shipped on one of the three screens this replaces, and each was fixed
 * once at the call site that caused it. Enforcing them AFTER the action rather
 * than inside it is what stops the next action from reintroducing one.
 */
/* The column vocabulary lives with the table that draws it. Imported rather
   than duplicated here: two lists of column keys would disagree on the first
   column anybody added. */
import { isDayKey } from '../../../../../function-utils/building-time';
import { columnKeysFor } from '../tabs/records-columns';
import { recordsBody } from '../data/records-api';

/* The page size a drill probe uses. It is only ever compared against itself, so
   the value does not matter — but it must be a CONSTANT, or two probes would
   differ for a reason that has nothing to do with the filter. */
const RECORDS_PROBE_PAGE_SIZE = 50;
import {
  ALL_BOOK_FIELDS,
  Book,
  BOOKS,
  DashboardFilter,
  CUSTOM_RANGE,
  Density,
  DENSITIES,
  DEFAULT_FILTER,
  Direction,
  DIRECTIONS,
  DrillSelection,
  FilterMeta,
  Granularity,
  GRANULARITIES,
  SortState,
  Status,
  STATUSES,
  Tab,
  TABS,
  bookCanFilter,
} from './filter-model';

/* ───────────────────────────────  actions  ────────────────────────────── */

export type MultiField = 'kinds' | 'groups' | 'actors' | 'seriesOff';

export type FilterAction =
  /** Replace wholesale — from the URL, or from a saved view. */
  | { type: 'hydrate'; filter: DashboardFilter }
  /** Generic patch. Everything below could be expressed as one; the named
   *  actions exist because they carry intent the chip row and the history
   *  behaviour need (a preset click pushes history, a scrub replaces it). */
  | { type: 'patch'; patch: Partial<DashboardFilter> }
  | { type: 'setScope'; scope: Partial<Pick<DashboardFilter, 'portfolioId' | 'buildingId' | 'unit'>> }
  | { type: 'setRange'; range: string }
  | { type: 'setCustomRange'; from: string; to: string }
  | { type: 'setGranularity'; granularity: Granularity }
  | { type: 'setCompare'; compare: string }
  | { type: 'setMeasure'; measure: string }
  | { type: 'setBook'; book: Book }
  | { type: 'toggleMulti'; field: MultiField; value: string }
  | { type: 'setTab'; tab: Tab }
  | { type: 'drill'; selection: DrillSelection; patch: Partial<DashboardFilter> }
  | { type: 'clearDrill' }
  | { type: 'setSort'; column: string }
  | { type: 'setPage'; page: number }
  | { type: 'setColumns'; columns: string[] | null }
  | { type: 'setDensity'; density: Density }
  | { type: 'setCompareScope'; scope: DashboardFilter['compareScope'] }
  /** Remove exactly one narrowing, by the patch the chip carries. */
  | { type: 'clearChip'; patch: Partial<DashboardFilter> }
  /** Every narrowing off. The window, the measure and the book stay — see below. */
  | { type: 'clearAll' }
  | { type: 'reset' };

/* ────────────────────────────  page behaviour  ────────────────────────── */

/**
 * Fields that do NOT change which rows come back.
 *
 * Everything else resets `page` to 0. Without that, narrowing a list you had
 * already paged into asks the server for rows 75-100 of a result that has six,
 * and widening it again silently keeps a page the reader never chose. The feed
 * already had to learn this (activity-feed.tsx:825-831); here it is derived
 * rather than remembered.
 */
const PAGE_NEUTRAL: (keyof DashboardFilter)[] = [
  'tab',
  'seriesOff',
  'density',
  'columns',
  'page',
  'compareScope',
  'qualityDrill',
];

const ROW_AFFECTING = (Object.keys(DEFAULT_FILTER) as (keyof DashboardFilter)[])
  .filter(key => !PAGE_NEUTRAL.includes(key));

const rowSignature = (filter: DashboardFilter): string =>
  JSON.stringify(ROW_AFFECTING.map(key => filter[key]));

/**
 * The narrowings "Clear all" clears, and the ones it does not.
 *
 * It clears everything that appears as a CHIP — the scope, the classification,
 * the book-specific criteria, the drill selection and the search term — because
 * that is what a reader means by clearing filters: "show me everything in this
 * window".
 *
 * It deliberately does NOT touch the range, the comparison, the granularity, the
 * measure or the book. Those are chrome controls with their own visible
 * affordance and their own current value; wiping a control the reader can see
 * set to something is how a Clear button becomes one nobody trusts. It is the
 * same distinction the reports drawer had to learn between Cancel and Clear all
 * (dashboard-reports.tsx:1210-1242).
 */
const CLEARABLE: (keyof DashboardFilter)[] = [
  'portfolioId',
  'buildingId',
  'unit',
  'timeFrom',
  'timeTo',
  'kinds',
  'severity',
  'groups',
  'actors',
  'role',
  'direction',
  'status',
  'priority',
  'buildingActivity',
  'visitorType',
  'visitorDetails',
  'preAuthorized',
  'onPass',
  'approval',
  'parkingValidation',
  'numberOfGuests',
  'carrier',
  'parcelStatus',
  'name',
  'lastName',
  'q',
  'drill',
];

export const hasAnyNarrowing = (filter: DashboardFilter): boolean =>
  CLEARABLE.some(key => {
    const value = filter[key];
    if (Array.isArray(value)) return value.length > 0;

    return value !== null && value !== '' && value !== undefined;
  });

/* ─────────────────────────────  normalise  ────────────────────────────── */

const oneOf = <T extends string>(
  value: T | null,
  allowed: readonly string[],
  fallback: T | null
): T | null => (value !== null && allowed.includes(value) ? value : fallback);

const toggle = (list: string[], value: string): string[] =>
  list.includes(value) ? list.filter(item => item !== value) : [...list, value];

/**
 * Write one field by name.
 *
 * Two rules below clear a SET of fields chosen at runtime — the book strip and
 * Clear all — so they index the filter with a variable, which the type system
 * cannot narrow. The cast is contained here rather than repeated at each site,
 * and it goes through `unknown` because `DashboardFilter` has no index
 * signature and TypeScript is right to say the two types do not overlap.
 */
const setField = (
  target: DashboardFilter,
  field: keyof DashboardFilter,
  value: unknown
): void => {
  (target as unknown as Record<string, unknown>)[field] = value;
};

/**
 * THE INVARIANTS. Run after every action, without exception.
 *
 * `meta` is optional: before it lands there is nothing to validate against and
 * a filter is assumed possible, exactly as the analytics screen assumes a panel
 * is possible until meta arrives (dashboard-analytics.tsx:695-697). Assuming the
 * opposite would blank the first paint.
 */
export const normalise = (
  input: DashboardFilter,
  meta?: FilterMeta,
  /**
   * The state BEFORE the action, when there was one.
   *
   * Three of the rules below are about a TRANSITION rather than about a value —
   * "the building changed", "the reader just set the other one of a mutually
   * exclusive pair", "the row set changed" — and a rule of that shape cannot be
   * expressed against the post-action object alone. Passing `prev` explicitly
   * is what keeps them honest; comparing `input` against a copy of itself is
   * always false, which is a rule that silently never fires.
   *
   * Absent (a hydrate from a URL, or a direct call in a test) those three rules
   * are skipped and the deterministic fallbacks below apply instead.
   */
  prev?: DashboardFilter
): DashboardFilter => {
  const next: DashboardFilter = { ...input };

  /* 1. A PORTFOLIO AND A SINGLE BUILDING ARE TWO NARROWINGS OF THE SAME ANSWER,
     and holding both silently answers a third question. Choosing one clears the
     other — the rule analytics-scope-bar.tsx:126-131 already applied. Whichever
     was set most recently wins, which the caller expresses by sending only that
     one in the patch. */
  if (next.portfolioId != null && next.buildingId != null) {
    // Both present after a hydrate (a hand-edited URL). The building is the
    // narrower claim, so it is the one that survives.
    next.portfolioId = null;
  }

  /* 2. A UNIT WITHOUT ITS BUILDING IS AMBIGUOUS — two buildings can both have a
     "10". It is still legal (the units endpoint is org-wide and the label
     carries the building name), but a unit CANNOT survive a change of building. */
  if (prev && next.buildingId !== prev.buildingId && next.unit === prev.unit) {
    next.unit = null;
  }

  /* 3. A CUSTOM RANGE NEEDS BOTH ENDS, AS REAL DAYS IN ORDER. Half a window is
     not a window — the hub refuses one outright ("`from_day` and `to_day` must
     be given together"). Both ends are inclusive day keys, so one day is a
     legal range (from === to); keys in 'YYYY-MM-DD' form compare as text. A
     named range carries no days. */
  if (next.range === CUSTOM_RANGE) {
    if (!isDayKey(next.from) || !isDayKey(next.to) || next.to < next.from) {
      next.range = meta?.defaultRange || DEFAULT_FILTER.range;
      next.from = null;
      next.to = null;
    }
  } else {
    next.from = null;
    next.to = null;
  }

  /* 4. A TIME-OF-DAY WINDOW MAY HAVE ONE END. Unlike a date range, "after
     09:00" and "before 12:00" are complete filters, and both servers read them
     that way (analytics entry-filters.js and reports.js, from inclusive, to
     exclusive). Requiring both ends made the rail unusable: each input
     dispatches on its own, so typing From was wiped before To could be typed
     (exports-filters suite, 2026-09-23). Only a malformed end is dropped. */
  const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (next.timeFrom && !HHMM.test(next.timeFrom)) next.timeFrom = null;
  if (next.timeTo && !HHMM.test(next.timeTo)) next.timeTo = null;

  /* 5. "STILL INSIDE" AND "GOING OUT" IS EMPTY BY DEFINITION, and an empty feed
     the reader did not ask for is indistinguishable from a quiet day. The tile
     strip already enforced this (activity-feed.tsx:759-788). */
  if (next.status && next.direction) {
    if (prev && prev.status === next.status) {
      // The direction is what just changed, so the direction wins.
      next.status = null;
    } else {
      // Either the status just changed, or there is no previous state to ask
      // (a hand-edited URL asking for both). The status is the narrower claim
      // and is the one that survives, deterministically.
      next.direction = null;
    }
  }

  /* 6. A FIELD THE SELECTED BOOK CANNOT FILTER ON IS NOT SENT. See BOOK_FIELDS —
     this is the invariant that makes the 2026-08-14 empty-report defect
     unrepeatable.

     THE BOOK IS VALIDATED FIRST, and the order is load-bearing rather than
     tidy. BOOK_FIELDS is keyed by the four legal book names, so a value that is
     not one of them — `?bk=Ledger`, one character of a hand-edited URL — makes
     the lookup `undefined` and the `.includes()` below a TypeError. There is not
     one error boundary in this application (see the same note in
     dashboard-logs.tsx), so that throw unmounts the entire root and the screen
     is blank. Validating here rather than with the other closed vocabularies
     further down is what keeps a typo a typo. */
  if (!BOOKS.includes(next.book)) next.book = DEFAULT_FILTER.book;

  for (const field of ALL_BOOK_FIELDS) {
    if (!bookCanFilter(next.book, field)) setField(next, field, null);
  }

  /* 7. VALIDATE AGAINST META. A value meta does not admit is dropped rather than
     sent — a stale bookmark falls back to the default instead of earning a 400,
     and a building id the caller cannot see is DROPPED rather than sent, because
     RLS would narrow the answer to nothing and the screen would show a confident
     set of zeros for a building that is simply not theirs. */
  if (meta) {
    if (!meta.ranges.includes(next.range) && next.range !== CUSTOM_RANGE) {
      next.range = meta.defaultRange;
      next.from = null;
      next.to = null;
    }
    if (!meta.compare.includes(next.compare)) next.compare = meta.defaultCompare;
    if (!meta.measures.some(m => m.key === next.measure)) {
      next.measure = meta.defaultMeasure;
    }
    if (!meta.latencySubjects.includes(next.subject)) {
      next.subject = meta.defaultSubject;
    }
    if (!meta.turnoverBuckets.includes(next.turnoverBucket)) {
      next.turnoverBucket = meta.defaultTurnoverBucket as Granularity;
    }
    next.severity = oneOf(next.severity, meta.severity, null);
    next.qualityDrill = oneOf(next.qualityDrill, meta.qualityDrills, null);
    next.kinds = next.kinds.filter(kind => meta.kinds.includes(kind));

    if (next.buildingId != null && !meta.buildings.some(b => b.id === next.buildingId)) {
      next.buildingId = null;
      next.unit = null;
    }
    if (next.portfolioId != null && !meta.portfolios.some(p => p.id === next.portfolioId)) {
      next.portfolioId = null;
    }
    if (next.compareScope) {
      const bad =
        (next.compareScope.buildingId != null &&
          !meta.buildings.some(b => b.id === next.compareScope?.buildingId)) ||
        (next.compareScope.portfolioId != null &&
          !meta.portfolios.some(p => p.id === next.compareScope?.portfolioId));
      if (bad) next.compareScope = null;
    }
  }

  /* 8. CLOSED VOCABULARIES THAT DO NOT COME OFF THE WIRE. */
  next.tab = (oneOf(next.tab, TABS, DEFAULT_FILTER.tab) || DEFAULT_FILTER.tab) as Tab;
  next.granularity = (oneOf(next.granularity, GRANULARITIES, DEFAULT_FILTER.granularity) ||
    DEFAULT_FILTER.granularity) as Granularity;
  next.density = (oneOf(next.density, DENSITIES, DEFAULT_FILTER.density) ||
    DEFAULT_FILTER.density) as Density;
  next.direction = oneOf(next.direction, DIRECTIONS, null) as Direction | null;
  next.status = oneOf(next.status, STATUSES, null) as Status | null;
  /* `book` is deliberately NOT here — it has to be validated before invariant 6
     can index BOOK_FIELDS with it. See the note there. */

  /* 9. A DRILL SELECTION WITHOUT ITS FILTER IS A CHIP THAT REMOVES NOTHING.
     Cleared alongside whatever it set, by the chip's own patch. */
  if (next.drill && !next.drill.key && !next.drill.label) next.drill = null;

  /* 9b. AN EMPTY COLUMN SET IS NOT A CHOICE, IT IS A TABLE WITH NO COLUMNS.
     `null` means "the book's default set" and `[]` would mean "hide everything",
     which no control can produce and which the URL cannot represent distinctly
     from absence. Collapsed here so the codec round-trips. */
  if (next.columns && next.columns.length === 0) next.columns = null;

  /* 9b-ii. A CHOSEN COLUMN SET BELONGS TO THE BOOK IT WAS CHOSEN FOR.
     `columns` survived a book change untouched, and the resolver intersects it
     with the new book's own columns — so picking `visitorType` and `approval` on
     the Visitor Log and switching to the Parcel Log produced a table with NO
     COLUMNS AT ALL. Pruned here rather than only at the resolver, so the URL and
     a saved view never carry a foreign key, and the column picker never shows a
     tick against a column this book does not have. */
  if (next.columns) {
    const legal = columnKeysFor(next.book);
    const kept = next.columns.filter(key => legal.includes(key));
    next.columns = kept.length ? kept : null;
  }

  /* 9c. TWO MORE SHAPES THAT CANNOT SURVIVE A ROUND TRIP THROUGH A URL, and so
     must not exist in the model either. A comparison scope that names nothing is
     not a comparison, and a sort with no column is not a sort — both encode to
     an absent parameter, so leaving them representable would mean a saved view
     that does not restore to itself. */
  if (
    next.compareScope &&
    next.compareScope.portfolioId == null &&
    next.compareScope.buildingId == null
  ) {
    next.compareScope = null;
  }
  if (next.sort && !next.sort.column) next.sort = null;

  /* 9d. THE TWO COMPARISONS ARE MUTUALLY EXCLUSIVE.
     "vs previous period" and "vs another building" are different questions, and
     with both on every panel carries four series — this place now, this place
     then, that place now, that place then — while every tile carries two deltas
     pointing at different things. The reader would have to work out which arrow
     meant what, on the screen whose whole argument is that they should not have
     to.

     It is also the request budget: a second scope already doubles the analytics
     requests against a pool of five, and a comparison window doubles the work
     inside each one. The two multipliers never combine.

     THE SCOPE WINS, because it is the one the reader just asked for — the
     control that sets it is a deliberate action, where `compare` defaults to on
     from the server descriptor and is usually inherited rather than chosen. This
     is enforced HERE rather than in the control so that a pasted link carrying
     both is corrected too. */
  if (next.compareScope && next.compare !== 'none') next.compare = 'none';

  /* 9e. COMPARING A SCOPE WITH ITSELF IS AN EMPTY QUESTION.
     It renders as two identical columns and a difference of zero on every tile,
     which looks exactly like a bug in the arithmetic. */
  if (
    next.compareScope &&
    next.compareScope.buildingId === next.buildingId &&
    next.compareScope.portfolioId === next.portfolioId
  ) {
    next.compareScope = null;
  }

  /* 10. PAGE RESETS WHEN THE ROW SET CHANGES. */
  if (prev && rowSignature(next) !== rowSignature(prev)) next.page = 0;
  if (!Number.isInteger(next.page) || next.page < 0) next.page = 0;

  return next;
};

/* ──────────────────────────────  apply  ───────────────────────────────── */

const apply = (
  state: DashboardFilter,
  action: FilterAction
): DashboardFilter => {
  switch (action.type) {
    case 'hydrate':
      return { ...action.filter };

    case 'patch':
      return { ...state, ...action.patch };

    case 'setScope': {
      const scope = action.scope;
      const next = { ...state, ...scope };
      // Explicit, rather than leaning on normalise's tie-break: naming a
      // portfolio clears the building and vice versa, and the caller does not
      // have to remember to send the null.
      if ('portfolioId' in scope && scope.portfolioId != null) next.buildingId = null;
      if ('buildingId' in scope && scope.buildingId != null) next.portfolioId = null;

      return next;
    }

    case 'setRange':
      return { ...state, range: action.range, from: null, to: null };

    case 'setCustomRange':
      return { ...state, range: CUSTOM_RANGE, from: action.from, to: action.to };

    case 'setGranularity':
      return { ...state, granularity: action.granularity };

    case 'setCompare':
      /* TURNING ON A PERIOD COMPARISON TURNS OFF A PLACE COMPARISON (WP-13).
         Invariant 9d below makes the two mutually exclusive and settles the tie
         in favour of the SCOPE — correct for a pasted URL carrying both, and
         wrong for a person pressing this control. The asymmetry made the "vs
         previous period" toggle a DEAD CONTROL whenever a compare scope was
         active: the action set `compare`, normalise immediately reset it to
         'none', and the control snapped back on every single click with no way
         to discover why.

         An explicit press is the one signal that should win, so it clears the
         other axis here. Setting it to 'none' clears nothing, because that is
         not a request for a different comparison. */
      return {
        ...state,
        compare: action.compare,
        compareScope: action.compare === 'none' ? state.compareScope : null,
      };

    case 'setMeasure':
      return { ...state, measure: action.measure };

    case 'setBook':
      // The book-specific criteria are cleared by normalise (invariant 6); the
      // window, the building and the time range survive, because somebody
      // switching from Visitors to Parcels is asking the same question of a
      // different table and re-picking the month is work the screen invented.
      return { ...state, book: action.book };

    case 'toggleMulti':
      return { ...state, [action.field]: toggle(state[action.field], action.value) };

    case 'setTab':
      return { ...state, tab: action.tab };

    case 'drill': {
      /* WHERE A DRILL LANDS DEPENDS ON WHAT IT IS, and getting this wrong broke
         a promise the panel makes in its own caveat.

         Choosing a BUILDING or a PORTFOLIO is not "show me these rows" — it is
         "scope the whole page to this place", which is what the league panel's
         caption says out loud. Sending the reader to the Records tab for it
         answered a question they did not ask and took away the panel they were
         reading. Every other dimension IS a row request: a kind, a severity, an
         hour, a day, a person — the reader has picked a slice and wants to see
         what is in it. */
      const scoping =
        action.selection.dimension === 'building'
        || action.selection.dimension === 'portfolio';

      /* ...AND ON WHETHER THE RECORDS TABLE CAN ACTUALLY HONOUR IT.

         Kind, severity and staff are row requests in intent, and the paragraph
         above is right about that — but POST /dashboard/reports takes none of
         those three. So clicking the Incident wedge, the single most valuable
         gesture on this screen, replaced the chart the reader was studying with
         a table of EVERY record in the period, under a chip saying "From a
         chart · Incident". They would have acted on the wrong rows.

         Landing them on an honest chart beats landing them on a dishonest
         table: the drill still narrows every panel, the chip still says what was
         clicked, and the Records tab is one click away with its own caveat now
         naming the filters it cannot apply (tabs/records-tab.tsx).

         ASKED OF THE REAL REQUEST BUILDER rather than a list of dimensions,
         because a list would have to be updated the day /dashboard/reports
         learns to take `severity` — and would not be. */
      const next = { ...state, ...action.patch };
      const reachesTheTable =
        JSON.stringify(recordsBody(next, RECORDS_PROBE_PAGE_SIZE))
        !== JSON.stringify(recordsBody(state, RECORDS_PROBE_PAGE_SIZE));

      return {
        ...next,
        drill: action.selection,
        tab: scoping || !reachesTheTable ? state.tab : 'records',
      };
    }

    case 'clearDrill':
      return { ...state, drill: null };

    case 'setSort': {
      const dir =
        state.sort && state.sort.column === action.column && state.sort.dir === 'asc'
          ? 'desc'
          : 'asc';
      const sort: SortState = { column: action.column, dir };

      return { ...state, sort };
    }

    case 'setPage':
      return { ...state, page: action.page };

    case 'setColumns':
      return { ...state, columns: action.columns };

    case 'setDensity':
      return { ...state, density: action.density };

    case 'setCompareScope':
      return { ...state, compareScope: action.scope };

    case 'clearChip':
      return { ...state, ...action.patch };

    case 'clearAll': {
      const next = { ...state };
      for (const key of CLEARABLE) {
        setField(next, key, Array.isArray(DEFAULT_FILTER[key]) ? [] : DEFAULT_FILTER[key]);
      }

      return next;
    }

    case 'reset':
      return { ...DEFAULT_FILTER };

    default:
      return state;
  }
};

export const filterReducer = (
  state: DashboardFilter,
  action: FilterAction,
  meta?: FilterMeta
): DashboardFilter =>
  // `state` is handed to normalise as `prev` so the three transition rules
  // (a changed building drops the unit, the mutually-exclusive pair resolves
  // toward whichever was just set, the page resets when the row set moves) have
  // something to compare against.
  //
  // EXCEPT for a wholesale replacement. `hydrate` and `reset` are not
  // transitions from the current view — they ARE the view, arriving from a URL,
  // a saved view or the back button — and treating them as a transition would
  // let the previous state edit the incoming one: a link carrying both a
  // building and a unit would land with the unit stripped, because the building
  // "changed". A pasted link has to restore exactly what it says.
  normalise(
    apply(state, action),
    meta,
    action.type === 'hydrate' || action.type === 'reset' ? undefined : state
  );

/**
 * Re-seed the server-owned defaults once meta lands, WITHOUT overwriting
 * anything the reader (or the URL) already chose.
 *
 * Called exactly once, on the transition from "no meta" to "meta". Applying it
 * on every meta render would undo a reader's choice the moment the descriptor
 * was refetched.
 */
export const seedFromMeta = (
  filter: DashboardFilter,
  meta: FilterMeta,
  touched: Set<keyof DashboardFilter>
): DashboardFilter => {
  const next = { ...filter };
  if (!touched.has('range') && filter.range === DEFAULT_FILTER.range) {
    next.range = meta.defaultRange;
  }
  if (!touched.has('compare') && filter.compare === DEFAULT_FILTER.compare) {
    next.compare = meta.defaultCompare;
  }
  if (!touched.has('measure') && filter.measure === DEFAULT_FILTER.measure) {
    next.measure = meta.defaultMeasure;
  }
  if (!touched.has('subject') && filter.subject === DEFAULT_FILTER.subject) {
    next.subject = meta.defaultSubject;
  }
  if (
    !touched.has('turnoverBucket') &&
    filter.turnoverBucket === DEFAULT_FILTER.turnoverBucket
  ) {
    next.turnoverBucket = meta.defaultTurnoverBucket as Granularity;
  }

  return normalise(next, meta);
};
